import React, { forwardRef, useImperativeHandle, useLayoutEffect, useState, type ReactNode } from 'react';
import styled from '@emotion/styled';
import Tooltip from 'components/common/tooltip';
import { typeStyle, type TypeStyle } from 'constants/typography';

type TextElement = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'div' | 'label' | 'code';

// Literal var() strings, so token-references.spec sees every one.
const TEXT_COLOR = {
  heading: 'var(--text-heading)',
  body: 'var(--text-body)',
  secondary: 'var(--text-secondary)',
  disabled: 'var(--text-disabled)',
  action: 'var(--text-action)',
  information: 'var(--text-information)',
  success: 'var(--text-success)',
  warning: 'var(--text-warning)',
  error: 'var(--text-error)',
  'tint-lime': 'var(--text-tint-lime)'
} as const;

export type TextColor = keyof typeof TEXT_COLOR;

const DEFAULT_ELEMENT: Record<TypeStyle, TextElement> = {
  headingH1: 'h1',
  headingH2: 'h2',
  headingH3: 'h3',
  headingH4: 'h4',
  bodyLg: 'p',
  bodyMd: 'p',
  bodySm: 'p',
  bodyLgBold: 'p',
  bodyMdBold: 'p',
  bodySmBold: 'p',
  labelMd: 'span',
  labelSm: 'span',
  overline: 'span',
  captionMd: 'span',
  captionSm: 'span',
  code: 'code'
};

export type TextProps = {
  variant: TypeStyle;
  as?: TextElement;
  color?: TextColor;
  ellipsis?: boolean | { lines: number };
  className?: string;
  'data-e2e'?: string;
  id?: string;
  children: ReactNode;
};

// text-overflow needs a block box: on an inline span it does nothing.
const truncation = (lines?: number) => {
  if (lines === 1)
    return 'display: block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
  if (lines)
    return `display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: ${lines}; overflow: hidden;`;
  return '';
};

// `as` stays out of the DOM and switches the element: emotion only honours it when shouldForwardProp
// refuses it.
const StyledText = styled('span', { shouldForwardProp: prop => prop !== 'as' && !prop.startsWith('$') })<{
  $variant: TypeStyle;
  $color?: TextColor;
  $lines?: number;
}>`
  margin: 0;
  /* antd Typography's: long URLs and IDs wrap instead of overflowing. */
  word-break: break-word;
  ${({ $variant }) => typeStyle[$variant]}
  ${({ $color }) => ($color ? `color: ${TEXT_COLOR[$color]};` : '')}
  ${({ $lines }) => truncation($lines)}
`;

// True once the rendered text is wider, or clamped taller, than its box.
const useIsTruncated = (element: HTMLElement | null, enabled: boolean) => {
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    if (!enabled || !element) return;

    const check = () =>
      setTruncated(element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight);
    check();

    const resize = new ResizeObserver(check);
    resize.observe(element);
    // New text in an unchanged box resizes nothing.
    const mutation = new MutationObserver(check);
    mutation.observe(element, { characterData: true, childList: true, subtree: true });
    // A webfont that lands after the first check widens the text, not the box. jsdom has no document.fonts.
    document.fonts?.addEventListener('loadingdone', check);

    return () => {
      resize.disconnect();
      mutation.disconnect();
      document.fonts?.removeEventListener('loadingdone', check);
    };
  }, [element, enabled]);

  return truncated;
};

/** The type ramp in JSX. Margins are reset; colour is inherited unless set, and headings default to the heading colour. */
export const Text = forwardRef<HTMLElement, TextProps>(({ variant, as, color, ellipsis, children, ...rest }, ref) => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  // The element is state, not a ref, so the truncation check re-runs when it mounts.
  useImperativeHandle(ref, () => element as HTMLElement, [element]);

  const lines = ellipsis === true ? 1 : ellipsis ? ellipsis.lines : undefined;
  const truncated = useIsTruncated(element, lines !== undefined);
  // Every antd Title rendered --text-heading; other variants inherit their container's colour.
  const resolvedColor = color ?? (variant.startsWith('heading') ? 'heading' : undefined);

  const text = (
    <StyledText
      ref={setElement}
      as={as ?? DEFAULT_ELEMENT[variant]}
      $variant={variant}
      $color={resolvedColor}
      $lines={lines}
      {...rest}>
      {children}
    </StyledText>
  );

  // The tooltip stays mounted while truncation is possible, so a resize does not remount the text.
  return lines === undefined ? (
    text
  ) : (
    <Tooltip title={truncated ? children : undefined} placement="top">
      {text}
    </Tooltip>
  );
});

Text.displayName = 'Text';

export default Text;
